import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Button, Modal, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Channel } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { useDraftSync } from "../lib/drafts.js";
import { useRealtimeFrames } from "../lib/realtime-context.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { NewChannelDialog } from "../components/NewChannelDialog.js";
import { ChannelList, FilterRail } from "./chat/ChannelList.js";
import { AddMembersDialog, EditChannelDialog } from "./chat/ChannelDialogs.js";
import { ChannelBrowserDialog, NewGroupDialog } from "./chat/ChannelBrowser.js";
import { ChannelSidePanels } from "./chat/DetailsPanel.js";
import { MessageActionsContext } from "./chat/message-actions.js";
import type { MessageActions } from "./chat/message-actions.js";
import { EditMessageDialog, ForwardDialog, RaiseTaskDialog } from "./chat/MessageDialogs.js";
import { ThreadPanel } from "./chat/ThreadPanel.js";
import { ThreadSidePanel } from "./chat/ThreadSidePanel.js";
import type { FilterKind, PendingMessage } from "./chat/types.js";
import { useChannelDetail } from "./chat/useChannelDetail.js";
import { useChannelMessages } from "./chat/useChannelMessages.js";
import { useChannels } from "./chat/useChannels.js";
import { useFileUploads } from "./chat/useFileUploads.js";
import { useMessageJump } from "./chat/useMessageJump.js";
import { useTypingUsers } from "./chat/useTypingUsers.js";
import "../components/linked-text.css";
import "./chat.css";

/**
 * The chat view's composition root: which channel is open, which panel and
 * dialog are showing, and the wiring between the sidebar (ChannelList), the
 * conversation (ThreadPanel) and the side column (ChannelSidePanels).
 *
 * Data lives in hooks under ./chat — useChannels for the list,
 * useChannelDetail for the open channel, useChannelMessages for its thread —
 * and per-message actions reach MessageRun through MessageActionsContext.
 */
export function Chat() {
  const { channelId } = useParams<{ channelId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const threadParam = searchParams.get("thread");
  const { viewer } = useAuth();
  const { toast } = useToast();

  const { channels, loading: channelsLoading, setChannels, markRead, touch, togglePin, reload } = useChannels(channelId);

  const handleMarkAllRead = useCallback(() => {
    api
      .post("/channels/read-all")
      .then(() => setChannels((prev) => prev.map((c) => (c.unreadCount ? { ...c, unreadCount: 0 } : c))))
      .catch((err: unknown) =>
        toast(err instanceof ApiError ? err.message : "Could not mark channels read.", { tone: "danger" }),
      );
  }, [setChannels, toast]);
  const {
    detail,
    loading: detailLoading,
    setDetail,
    reload: loadDetail,
    setNotificationLevel,
  } = useChannelDetail(channelId);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useDraftSync();
  const thread = useChannelMessages(channelId, { scrollRef, onRead: markRead, onActivity: touch });
  const typingNames = useTypingUsers(channelId);
  const files = useFileUploads(channelId, thread.reloadLatest);
  const { jumpedId, jumpTo } = useMessageJump(channelId, thread);

  /** Which slice of the sidebar is showing — owned here so the filter rail and
   *  the channel list beside it share one selection. */
  const [activeFilterTab, setActiveFilterTab] = useState<FilterKind>("chats");
  const [detailsOpen, setDetailsOpen] = useState(false);
  /** The root of the reply thread open in the side column, if any. */
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  /** Set to a message to raise a task from it, or `null` for a bare task
   *  raised from the composer with no source message. */
  const [raisingFrom, setRaisingFrom] = useState<PendingMessage | null | undefined>(undefined);
  /** The message the composer is currently replying to, if any. */
  const [replyingTo, setReplyingTo] = useState<PendingMessage | null>(null);
  /** The message being edited in place, and the one awaiting delete confirmation. */
  const [editingMessage, setEditingMessage] = useState<PendingMessage | null>(null);
  const [deletingMessage, setDeletingMessage] = useState<PendingMessage | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** The message the "Forward to…" dialog is currently sending. */
  const [forwardingMessage, setForwardingMessage] = useState<PendingMessage | null>(null);
  const [newChannelOpen, setNewChannelOpen] = useState(false);
  const [editChannelOpen, setEditChannelOpen] = useState(false);
  const [deleteChannelOpen, setDeleteChannelOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [removingMember, setRemovingMember] = useState<{ id: string; displayName: string } | null>(null);
  const [deletingChannel, setDeletingChannel] = useState(false);
  /** Set while a direct conversation is being opened from the search results. */
  const [openingPersonId, setOpeningPersonId] = useState<string | null>(null);

  // An archived channel drops out of the list but is still open on screen, so
  // fall back to what the detail knows rather than showing "Loading…".
  const activeChannel = useMemo(
    () =>
      channels.find((c) => c.id === channelId) ??
      (detail && detail.channel.id === channelId
        ? ({ ...detail.channel, topic: detail.channel.topic ?? "", lastMessageAt: null } as Channel)
        : undefined),
    [channels, channelId, detail],
  );

  // Someone archived, restored or retitled the open channel: re-read it.
  useRealtimeFrames((frame) => {
    if (frame.t === "channel_updated" && frame.channelId === channelId) loadDetail();
  });
  // Empty while nothing is open, or while the sidebar's own channel list is
  // still loading — usePageTitle skips an empty name, so the tab keeps
  // whatever it already showed rather than flashing "Chat" for a moment.
  usePageTitle(activeChannel?.name ?? (channelId ? "" : "Chat"));

  // A pending reply belongs to the thread it was started in; carrying it
  // across would attach it to a message the new channel does not contain.
  useEffect(() => {
    setReplyingTo(null);
  }, [channelId, viewer]);

  // A thread belongs to the channel it was opened in.
  useEffect(() => {
    setOpenThreadId(null);
  }, [channelId]);

  // `?thread=` is how the Threads inbox asks for a panel in another channel;
  // consumed once so reopening the same thread later still works.
  useEffect(() => {
    if (!threadParam || !channelId) return;
    setOpenThreadId(threadParam);
    setDetailsOpen(false);
    setSearchOpen(false);
    navigate(`/chat/${channelId}`, { replace: true });
  }, [threadParam, channelId, navigate]);

  const openThread = useCallback((message: PendingMessage) => {
    // A reply opens the thread it belongs to; threads are one level deep.
    setOpenThreadId(message.inThread && message.parentMessageId ? message.parentMessageId : message.id);
    setDetailsOpen(false);
    setSearchOpen(false);
  }, []);

  const handleOpenThread = useCallback(
    (threadChannelId: string, rootId: string) => navigate(`/chat/${threadChannelId}?thread=${rootId}`),
    [navigate],
  );

  const handleSelectChannel = useCallback(
    (id: string, messageId?: string) => {
      // The `?message=` param is picked up by useMessageJump once this
      // channel's initial page has loaded, the same way a notification's
      // deep link does.
      navigate(messageId ? `/chat/${id}?message=${messageId}` : `/chat/${id}`);
    },
    [navigate],
  );

  const deleteChannel = useCallback(() => {
    if (!channelId) return;
    setDeletingChannel(true);
    api
      .delete(`/channels/${channelId}`)
      .then(() => {
        setChannels((prev) => prev.filter((c) => c.id !== channelId));
        setDeleteChannelOpen(false);
        // The open thread no longer exists, so staying on its URL would show
        // a permanent "not found".
        navigate("/chat", { replace: true });
        toast("Channel deleted. Its tasks and files were kept.");
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not delete that channel.", { tone: "danger" });
      })
      .finally(() => setDeletingChannel(false));
  }, [channelId, navigate, setChannels, toast]);

  /**
   * Opens the conversation with someone picked out of the search results.
   *
   * The endpoint is idempotent, so this lands in the existing thread when
   * there is one and creates it otherwise — the caller does not need to know
   * which case it is in.
   */
  const handleMessagePerson = useCallback(
    (personId: string) => {
      setOpeningPersonId(personId);
      api
        .post<{ channel: Channel; created: boolean }>("/channels/direct", { userId: personId })
        .then((res) => {
          // Added locally so the thread has a sidebar entry immediately; the
          // next load picks it up from the server either way.
          setChannels((prev) => (prev.some((c) => c.id === res.channel.id) ? prev : [res.channel, ...prev]));
          handleSelectChannel(res.channel.id);
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not open that conversation.", { tone: "danger" });
        })
        .finally(() => setOpeningPersonId(null));
    },
    [handleSelectChannel, setChannels, toast],
  );

  const { send } = thread;
  const sendMessage = useCallback(
    (body: string) => {
      if (!channelId || !viewer) return;
      // The reply is captured into the optimistic row, so the banner can be
      // cleared immediately without the in-flight request losing its parent.
      send(body, replyingTo);
      setReplyingTo(null);
    },
    [channelId, viewer, send, replyingTo],
  );

  const deleteMessage = useCallback(async () => {
    if (!deletingMessage) return;
    setDeleting(true);
    if (await thread.deleteMessage(deletingMessage.id)) setDeletingMessage(null);
    setDeleting(false);
  }, [deletingMessage, thread]);

  const isProjectChannel = activeChannel?.kind === "project";
  // Pinning and replying are for members; reading an open channel is not enough.
  const isMember = !!viewer && (detail?.members.some((m) => m.id === viewer.id) ?? false);

  const messageActions = useMemo<MessageActions>(
    () => ({
      toggleReaction: thread.toggleReaction,
      raiseTask: setRaisingFrom,
      reply: setReplyingTo,
      edit: setEditingMessage,
      delete: setDeletingMessage,
      setFlag: thread.setFlag,
      remind: thread.remind,
      forward: setForwardingMessage,
      retry: thread.retry,
      openThread,
      togglePin: thread.togglePin,
      toggleSaved: thread.toggleSaved,
      removePreview: thread.removePreview,
      viewerId: viewer?.id,
      canRaiseTask: isProjectChannel,
      canPin: isMember,
      readWatermark: thread.readWatermark,
      jumpedId,
    }),
    [
      thread.toggleReaction,
      thread.setFlag,
      thread.remind,
      thread.retry,
      thread.togglePin,
      thread.toggleSaved,
      thread.removePreview,
      thread.readWatermark,
      openThread,
      viewer?.id,
      isProjectChannel,
      isMember,
      jumpedId,
    ],
  );

  return (
    <div className={`chat-shell ${channelId ? "has-active-channel" : ""}`}>
      <FilterRail active={activeFilterTab} onChange={setActiveFilterTab} />
      <ChannelList
        channels={channels}
        loading={channelsLoading}
        activeChannelId={channelId}
        onSelect={handleSelectChannel}
        onNewChannel={() => setNewChannelOpen(true)}
        onBrowseChannels={() => setBrowserOpen(true)}
        onNewGroup={() => setNewGroupOpen(true)}
        onMarkAllRead={handleMarkAllRead}
        onMessagePerson={handleMessagePerson}
        openingPersonId={openingPersonId}
        activeTab={activeFilterTab}
        onTogglePin={togglePin}
        onOpenThread={handleOpenThread}
      />

      <MessageActionsContext.Provider value={messageActions}>
        <ThreadPanel
          channelId={channelId}
          channel={activeChannel}
          detail={detail}
          thread={thread}
          scrollRef={scrollRef}
          typingNames={typingNames}
          files={files}
          replyingTo={replyingTo}
          onCancelReply={() => setReplyingTo(null)}
          onSend={sendMessage}
          onCreateTask={() => setRaisingFrom(null)}
          searchOpen={searchOpen}
          detailsOpen={detailsOpen}
          onToggleSearch={() => {
            setSearchOpen((open) => !open);
            // One panel at a time: they occupy the same column, and
            // both open would stack a search box on top of members.
            setDetailsOpen(false);
            setOpenThreadId(null);
          }}
          onToggleDetails={() => {
            setDetailsOpen((v) => !v);
            setOpenThreadId(null);
          }}
        />
      </MessageActionsContext.Provider>

      {openThreadId && channelId && (
        <ThreadSidePanel
          channelId={channelId}
          rootId={openThreadId}
          members={detail?.members ?? []}
          canReply={isMember}
          viewerId={viewer?.id}
          onClose={() => setOpenThreadId(null)}
        />
      )}

      {/* Shown for any channel — a team channel still has members, a topic,
          and manage actions. Direct conversations have none of those. */}
      {activeChannel && activeChannel.kind !== "direct" && channelId && (
        <ChannelSidePanels
          channelId={channelId}
          searchOpen={searchOpen}
          onCloseSearch={() => setSearchOpen(false)}
          onJump={jumpTo}
          detailsOpen={detailsOpen}
          onCloseDetails={() => setDetailsOpen(false)}
          detail={detail}
          loading={detailLoading}
          onEdit={() => setEditChannelOpen(true)}
          onDelete={() => setDeleteChannelOpen(true)}
          onChangeNotificationLevel={setNotificationLevel}
          onAddMembers={() => setAddMembersOpen(true)}
          onRemoveMember={setRemovingMember}
          onSaveAbout={async (changes) => {
            try {
              await api.patch(`/channels/${channelId}/about`, changes);
              loadDetail();
              reload();
            } catch (error) {
              toast(error instanceof ApiError ? error.message : "Could not save that.", { tone: "danger" });
              throw error;
            }
          }}
          onToggleArchive={() => {
            const archiving = !detail?.channel.archivedAt;
            api
              .post(`/channels/${channelId}/${archiving ? "archive" : "unarchive"}`)
              .then(() => {
                loadDetail();
                reload();
                toast(archiving ? "Channel archived. It is now read-only." : "Channel restored.");
              })
              .catch((error: unknown) =>
                toast(error instanceof ApiError ? error.message : "Could not change that channel.", { tone: "danger" }),
              );
          }}
          onLeave={() => setLeaveOpen(true)}
        />
      )}

      <Modal open={browserOpen} onClose={() => setBrowserOpen(false)} title="Browse channels">
        <ChannelBrowserDialog
          onChanged={reload}
          onOpen={(id) => {
            setBrowserOpen(false);
            handleSelectChannel(id);
          }}
        />
      </Modal>

      <Modal open={newGroupOpen} onClose={() => setNewGroupOpen(false)} title="New group message">
        <NewGroupDialog
          onClose={() => setNewGroupOpen(false)}
          onCreated={(channel) => {
            setChannels((prev) => (prev.some((c) => c.id === channel.id) ? prev : [channel, ...prev]));
            handleSelectChannel(channel.id);
          }}
        />
      </Modal>

      <Modal open={leaveOpen} onClose={() => setLeaveOpen(false)} title="Leave this conversation?">
        <p>You will stop receiving its messages. {detail?.channel.isPrivate ? "You cannot rejoin unless someone adds you back." : "You can rejoin from Browse channels."}</p>
        <div className="chat-edit__actions">
          <Button variant="ghost" onClick={() => setLeaveOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (!channelId || !viewer) return;
              api
                .delete(`/channels/${channelId}/members/${viewer.id}`)
                .then(() => {
                  setLeaveOpen(false);
                  setChannels((prev) => prev.filter((c) => c.id !== channelId));
                  navigate("/chat", { replace: true });
                })
                .catch((error: unknown) =>
                  toast(error instanceof ApiError ? error.message : "Could not leave.", { tone: "danger" }),
                );
            }}
          >
            Leave
          </Button>
        </div>
      </Modal>

      <Modal
        open={raisingFrom !== undefined}
        onClose={() => setRaisingFrom(undefined)}
        title={raisingFrom ? "Turn this into a task" : "New task"}
      >
        <RaiseTaskDialog
          projectId={detail?.channel.projectId ?? null}
          sourceMessage={raisingFrom ?? undefined}
          onClose={() => setRaisingFrom(undefined)}
          onCreated={(task, sourceMessageId) => {
            // The card is attached locally rather than refetching the thread:
            // the server has already linked it, and a refetch would scroll the
            // conversation out from under whoever just raised it.
            if (!sourceMessageId) return;
            thread.attachTask(sourceMessageId, task);
          }}
        />
      </Modal>

      {/* Channels only — a channel needs a name, topic, and project, which a
          search box cannot capture. People are reached by searching the
          sidebar directly. */}
      <NewChannelDialog
        open={newChannelOpen}
        onClose={() => setNewChannelOpen(false)}
        onCreated={(channel) => {
          setChannels((prev) => [channel, ...prev]);
          handleSelectChannel(channel.id);
        }}
      />

      {detail && (
        <Modal open={editChannelOpen} onClose={() => setEditChannelOpen(false)} title="Edit channel">
          <EditChannelDialog
            channel={detail.channel}
            onClose={() => setEditChannelOpen(false)}
            onSaved={(changes) => {
              // Patched locally rather than refetching: the header, the
              // sidebar row, and the panel all read the same two fields, and
              // a refetch would scroll the thread.
              // `topic` is a plain string on the shared Channel type; an empty
              // one is the absence, so null collapses to "".
              setDetail((prev) =>
                prev ? { ...prev, channel: { ...prev.channel, name: changes.name, topic: changes.topic ?? "" } } : prev,
              );
              setChannels((prev) =>
                prev.map((c) => (c.id === detail.channel.id ? { ...c, name: changes.name, topic: changes.topic ?? "" } : c)),
              );
            }}
          />
        </Modal>
      )}

      <Modal open={addMembersOpen} onClose={() => setAddMembersOpen(false)} title="Add people to this channel">
        <AddMembersDialog
          existingIds={new Set((detail?.members ?? []).map((member) => member.id))}
          onClose={() => setAddMembersOpen(false)}
          onAdd={async (userIds) => {
            if (!channelId) return;
            try {
              await api.post(`/channels/${channelId}/members`, { userIds });
              setAddMembersOpen(false);
              loadDetail();
              // The sidebar carries its own count and is not re-fetched here,
              // so it is moved in place rather than left a person behind.
              setChannels((previous) =>
                previous.map((channel) =>
                  channel.id === channelId
                    ? { ...channel, memberCount: (channel.memberCount ?? 0) + userIds.length }
                    : channel,
                ),
              );
              toast(userIds.length === 1 ? "Added them to the channel." : `Added ${userIds.length} people.`);
            } catch (error) {
              toast(error instanceof ApiError ? error.message : "Could not add them.", { tone: "danger" });
            }
          }}
        />
      </Modal>

      {forwardingMessage && (
        <Modal open onClose={() => setForwardingMessage(null)} title="Forward to…">
          <ForwardDialog
            channels={channels}
            message={forwardingMessage}
            onClose={() => setForwardingMessage(null)}
            onForward={async (target, note) => {
              // A person picked from search may have no thread yet — opened
              // (or created) the same idempotent way starting a new
              // conversation from the sidebar does, before forwarding into it.
              const destinationChannelId = target.channelId
                ? target.channelId
                : (
                    await api.post<{ channel: Channel; created: boolean }>("/channels/direct", {
                      userId: target.userId,
                    })
                  ).channel.id;

              await api.post(`/channels/${destinationChannelId}/messages/forward`, {
                messageId: forwardingMessage.id,
                ...(note ? { note } : {}),
              });

              toast("Message forwarded.");

              if (!channels.some((c) => c.id === destinationChannelId)) {
                setChannels((prev) => [
                  { id: destinationChannelId, projectId: null, name: "", topic: "", kind: "direct", isPrivate: true, lastMessageAt: new Date().toISOString() },
                  ...prev,
                ]);
              }
            }}
          />
        </Modal>
      )}

      <Modal
        open={!!removingMember}
        onClose={() => setRemovingMember(null)}
        title={`Remove ${removingMember?.displayName ?? "this person"}?`}
      >
        <p className="chat-confirm__body">
          They will stop receiving messages from this channel and will not be able to post in it.
          {detail?.channel.isPrivate
            ? " Because this channel is private, they will no longer see it at all."
            : " They can still read it, since this channel is open to the workspace."}
        </p>
        <div className="chat-confirm__actions">
          <Button variant="ghost" onClick={() => setRemovingMember(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (!channelId || !removingMember) return;
              const target = removingMember;
              void api
                .delete(`/channels/${channelId}/members/${target.id}`)
                .then(() => {
                  setRemovingMember(null);
                  loadDetail();
                  setChannels((previous) =>
                    previous.map((channel) =>
                      channel.id === channelId
                        ? { ...channel, memberCount: Math.max(0, (channel.memberCount ?? 1) - 1) }
                        : channel,
                    ),
                  );
                  toast(`Removed ${target.displayName}.`);
                })
                .catch((error: unknown) => {
                  toast(error instanceof ApiError ? error.message : "Could not remove them.", { tone: "danger" });
                });
            }}
          >
            Remove
          </Button>
        </div>
      </Modal>

      <Modal open={deleteChannelOpen} onClose={() => setDeleteChannelOpen(false)} title="Delete this channel?">
        <div className="chat-confirm">
          <p>
            <strong>#{detail?.channel.name}</strong> and its messages will be removed. Tasks and files raised from it
            are kept — they outlive the conversation that produced them. This cannot be undone.
          </p>
          <div className="chat-confirm__actions">
            <Button variant="ghost" onClick={() => setDeleteChannelOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={deletingChannel} onClick={deleteChannel}>
              Delete channel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!editingMessage}
        onClose={() => setEditingMessage(null)}
        title="Edit message"
      >
        {editingMessage && (
          <EditMessageDialog
            message={editingMessage}
            onClose={() => setEditingMessage(null)}
            onSaved={(updated) => {
              thread.replaceMessage(updated);
              setEditingMessage(null);
            }}
          />
        )}
      </Modal>

      <Modal open={!!deletingMessage} onClose={() => setDeletingMessage(null)} title="Delete this message?">
        <div className="chat-confirm">
          <p>
            It will be replaced with “Message deleted” for everyone in this conversation. Replies to it stay, so the
            thread still reads — but what you wrote is gone and cannot be brought back.
          </p>
          <div className="chat-confirm__actions">
            <Button variant="ghost" onClick={() => setDeletingMessage(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => void deleteMessage()}>
              Delete message
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
